import React, { useState, useEffect } from 'react';
import styles from "./styles.module.css";
import DatabaseService from "../services/database.service.js";
import DeviceModalSettings from './devices/DeviceModalSettings.js';
import DeviceModalLogs from './devices/DeviceModalLogs.js';
import { convertDateToString } from '../common/ConvertTools.js'
import {
  DataGrid,
  GridToolbarContainer,
  GridToolbarColumnsButton,
  GridToolbarFilterButton,
  GridToolbarExport,
  GridToolbarQuickFilter
} from '@mui/x-data-grid';
import EventBus from "../common/EventBus.js";
import AuthService from "../services/auth.service.js";
import { ToastContainer, toast } from 'react-toastify';
import '../services/isMobile.js';

import gridSxStyle from "../common/GridSxStyles.js";

const BoardDiag = () => {
  const [devices, setDevices] = useState([]);
  const [firmwares, setFirmwares] = useState([]);
  const DEFAULT_SORT_MODEL = [{ field: 'date_app_cnx', sort: 'desc' }];
  const storedSortModel = localStorage.getItem('devicesSortModel');
  const initialSortModel = storedSortModel ? JSON.parse(storedSortModel) : DEFAULT_SORT_MODEL;
  const [devicesSortModel, setDevicesSortModel] = useState(initialSortModel);
  const DEFAULT_PAGINATION_MODEL = { page: 0, pageSize: 45 };
  const storedPaginationModel = localStorage.getItem('devicesPaginationModel');
  const initialPaginationModel = storedPaginationModel ? JSON.parse(storedPaginationModel) : DEFAULT_PAGINATION_MODEL;
  const [devicesPaginationModel, setDevicesPaginationModel] = useState(initialPaginationModel);
  const currentUser = AuthService.getCurrentUser();
  const isAdmin = currentUser.roles.includes("admin_smart");
  const isPartner = currentUser.roles.includes("partner");
  const isDesktop = !window.mobileCheck();
  const [isModalSettingsOpen, setModalSettingsOpen] = useState(false);
  const [isModalLogsOpen, setModalLogsOpen] = useState(false);
  const handleCloseSettingsModal = () => setModalSettingsOpen(false);
  const handleCloseLogsModal = () => setModalLogsOpen(false);
  const [selectedDevice, setSelectedDevice] = useState(null);
  let toastId = null;
  const minToastDuration = 1500; // Minimum toast display time in milliseconds
  const [errors, setErrors] = useState([]);

  const showToast = () => {
    toastId = toast.loading("Refreshing", {
      position: "bottom-right",
      autoClose: false, // Prevent auto-close to manually control the duration
    });
  };

  const TableToolbar = (props) => {
    return (
      <GridToolbarContainer>
        <button className="MuiButtonBase-root MuiButton-root MuiButton-text MuiButton-textPrimary MuiButton-sizeSmall MuiButton-textSizeSmall MuiButton-root MuiButton-text MuiButton-textPrimary MuiButton-sizeSmall MuiButton-textSizeSmall MuiCustomButton"
          onClick={() => {
            showToast();
            loadDevices(true);
          }}>
          <i className="fa-solid fa-arrows-rotate" style={{ color: 'var(--color-foreground)', marginRight: '8px' }}></i>
          Refresh
        </button>
        <GridToolbarColumnsButton />
        <GridToolbarFilterButton />
        <GridToolbarExport />
        <GridToolbarQuickFilter />
      </GridToolbarContainer>
    );
  };

  const loadDevices = async (withNotif = false) => {
    const startTime = Date.now();
    try {
      const firmwareResponse = await DatabaseService.getFirmwares();
      const deviceResponse = await DatabaseService.getDevices();
      const errorResponse = await DatabaseService.getLogsErrors();

      setFirmwares(firmwareResponse.data);
      setErrors(errorResponse.data);

      const devicesWithErrors = deviceResponse.data.filter(device => getDeviceErrors(errorResponse.data, device.mac_custom, device.ack_errors_before) !== "");
      setDevices(devicesWithErrors);

      if (withNotif) {
        const loadDuration = Date.now() - startTime;
        const displayDuration = Math.max(minToastDuration, loadDuration);
        setTimeout(() => {
          toast.dismiss(toastId);
        }, displayDuration);
      }
    } catch (error) {
      if (error.response && error.response.status === 401) {
        EventBus.dispatch("logout");
      }
      console.error(error);
    }
  };

  useEffect(() => {
    loadDevices();
  }, []);

  function convertVersionToString(version) {
    return version === undefined || version === 0 || isNaN(version) ? "" : version;
  }

  const handleOpenSettingsModal = (deviceProps) => {
    setSelectedDevice(deviceProps);
    setModalSettingsOpen(true);
  };

  const handleOpenLogsModal = (deviceProps) => {
    setSelectedDevice(deviceProps);
    setModalLogsOpen(true);
  };

  const handleSortModelChange = (sortModel) => {
    setDevicesSortModel(sortModel);
    localStorage.setItem('devicesSortModel', JSON.stringify(sortModel));
  };

  const handlePaginationModelChange = (paginationModel) => {
    setDevicesPaginationModel(paginationModel);
    localStorage.setItem('devicesPaginationModel', JSON.stringify(paginationModel));
  };

  const processRowUpdate = React.useCallback(
    async (newRow) => {
      const response = await DatabaseService.editDeviceComment(newRow.id, newRow.comment);
      if (response.status === 204) {
        toast.success("Comment edited successfully", {
          position: "bottom-right",
          autoClose: 2000
        });
        const updatedRows = devices.map((item) => item.id === newRow.id ? { ...item, comment: newRow.comment } : item);
        setDevices(updatedRows);
      } else {
        toast.error("Error editing the comment", {
          position: "bottom-right",
          autoClose: 2000
        });
      }
      return response;
    },
    [devices]
  );

  const handleProcessRowUpdateError = React.useCallback((error) => {
    console.log("handleProcessRowUpdateError", error);
  }, []);

  const getDeviceErrors = (errors, mac_custom, ack_errors_before) => {

    if (!mac_custom) {
      return "";
    }

    if (isNaN(ack_errors_before)) {
      ack_errors_before = 0;
    }

    // Convert ack_errors_before to milliseconds
    const ackErrorsBeforeMs = ack_errors_before * 1000;

    return errors
      .filter(error =>
        error.mac_custom.toLowerCase() === mac_custom.toLowerCase() &&
        error.date > ackErrorsBeforeMs // Only include errors after the ack_errors_before timestamp
      )
      .map(error =>
        `${convertDateToString(error.date / 1000, true)} - ${error.text.replace("new error state / ", "")}`
      )
      .join('\n');
  };


  const handleAcckErrors = async (row) => {

    const confirmed = window.confirm('Are you sure you want to acknowledge those errors ?');
    if (confirmed) {
      const response = await DatabaseService.editAckErrorsBefore(row.id, (Date.now() / 1000).toFixed(0))

      console.log("handleAcckErrors", response);
      if (response.status === 204) {
        toast.success("Errors acknowledged successfully", {
          position: "bottom-right",
          autoClose: 2000
        });
      } else {
        toast.error("Errors acknowledging the errors", {
          position: "bottom-right",
          autoClose: 2000
        });
      }
    }
  }

  const columns = [
    {
      field: 'actions', headerName: '',
      width: 140,
      resizable: false,
      renderCell: ({ row }) => (
        <div style={{ textAlign: 'center' }}>
          <i className="fa fa-gear" style={{ marginTop: "1px" }} onClick={() => { handleOpenSettingsModal(row) }}></i>
          {(isAdmin || isPartner) && <i className="fa fa-file-lines"  style={{ paddingLeft: "15px" }} onClick={() => { handleOpenLogsModal(row) }}></i>}
          {(isAdmin || isPartner) && <i className="fa fa-circle-check"  style={{ paddingLeft: "15px" }} onClick={() => { handleAcckErrors(row) }}></i>}
        </div>
      )
    },
    { field: 'ble_name', headerName: 'Smart Name', width: 180 },
    { field: 'user_id', headerName: 'Email', width: 250 },
    { field: 'comment', headerName: 'Comment', hidden: true, editable: true },
    {
      field: 'date_app_cnx', headerName: 'APP cnx', width: 150,
      renderCell: ({ row }) => (<div>{convertDateToString(row.date_app_cnx)}</div>)
    },
    {
      field: 'version_app', headerName: 'APP version', width: 80,
      renderCell: ({ row }) => (<div>{convertVersionToString(row.version_app)}</div>)
    },
    {
      field: 'date_update', headerName: 'FW update', width: 150,
      renderCell: ({ row }) => (<div>{convertDateToString(row.date_update)}</div>)
    },
    { field: 'version', headerName: 'FW version', width: 80 },
    { field: 'type_str', headerName: 'FW type1', width: 250 },
    {
      field: 'type_id', headerName: 'FW type2', width: 130,
      renderCell: ({ row }) => (<div>{firmwares.find(firmware => firmware.id === row.type_id)?.controller}</div>)
    },
    { field: 'mac_custom', headerName: 'MAC custom', width: 150 },
    {
      field: 'errors', headerName: 'Errors', flex: 1, minWidth: 500,
      renderCell: ({ row }) => (<div style={{ whiteSpace: 'pre-wrap' }}>{getDeviceErrors(errors, row.mac_custom, row.ack_errors_before)}</div>)
    },
  ];

  return (
    <div className={styles.Container} style={{ flex: "1" }}>
      <ToastContainer />
      <div className="row">
        <DataGrid
          getRowId={(row) => { return row.mac_custom ? row.mac_custom : "unused" }}
          processRowUpdate={processRowUpdate}
          onProcessRowUpdateError={handleProcessRowUpdateError}
          columnHeaderHeight={35}
          getRowHeight={() => 'auto'}
          rows={devices}
          columns={columns}
          initialState={{
            sorting: { sortModel: devicesSortModel },
            pagination: { paginationModel: devicesPaginationModel },
            columns: {
              columnVisibilityModel: {
                "comment": isAdmin,
                "date_app_cnx": isAdmin || isPartner,
                "version_app": isAdmin || isPartner,
                "user_id": isAdmin || isPartner,
              },
            },
          }}
          onSortModelChange={handleSortModelChange}
          pageSizeOptions={[25, 30, 35, 40, 45, 50, 55, 60, 100]}
          onPaginationModelChange={handlePaginationModelChange}
          slots={{ toolbar: TableToolbar }}
          slotProps={{
            toolbar: {
              showQuickFilter: true,
              quickFilterProps: { debounceMs: 500 },
            },
          }}
          sx={gridSxStyle}
        />
        {isModalSettingsOpen &&
          <DeviceModalSettings
            isOpen={isModalSettingsOpen}
            isAdmin={isAdmin}
            onRequestClose={handleCloseSettingsModal}
            deviceProps={selectedDevice}
          />
        }
        {((isAdmin || isPartner) && isModalLogsOpen) &&
          <DeviceModalLogs
            isOpen={isModalLogsOpen}
            isAdmin={isAdmin}
            onRequestClose={handleCloseLogsModal}
            deviceProps={selectedDevice}
            isDesktop={isDesktop}
          />
        }
      </div >
    </div >
  );
};

export default BoardDiag;
